<script>
import { uiState } from "@app/store/uiStore.svelte.js";
import { modalState, showModal, hideModal } from "@app/store/modalStore.svelte.js";
import { externalDataState, setExternalDataState } from "@app/store/externalDataStore.svelte.js";
import { toast } from '@zerodevx/svelte-toast';
import { fade } from "svelte/transition";
import { projectState, resetProjectState, updateProjectMedia } from "@app/store/projectStore.svelte.js";
import { updateDuration } from "@app/store/playerStore.svelte.js";
import { resetMarkerState, resetMarkerStateWithData } from '@app/store/markerStore.svelte.js';
import { issueState } from '@app/store/issueStore.svelte.js';
import { speakerState } from "@app/store/speakerStore.svelte.js";
import { metadataState } from "@app/store/metadataStore.svelte.js";
import { eventGroupState, resetEventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { onMount } from "svelte";
import * as m from "@app/paraglide/messages.js";

/* Firebase */
import { auth, functions } from '@app/configs/firebase.js';
import { httpsCallable } from 'firebase/functions';

import _Event from "@cclib/classes/event.js";
import _EventGroup from "@cclib/classes/eventGroup.js";
import _Project from "@cclib/classes/project.js";

let progress = $state(0),
    statusMsg = $state(m.iconik_import_status_starting());

async function updateStatus(statusInfo) {
    progress = statusInfo.progress;
    statusMsg = statusInfo.msg;
}

onMount(async () => {
    try {
        let projectInfo, assetId;

        await updateStatus({
            progress: 10,
            msg: m.iconik_import_status_receiving(),
        });

        projectState.selected = false;
        resetEventGroupState();
        issueState.length = 0;
        resetMarkerState();
        speakerState.length = 0;
        metadataState.length = 0;
        uiState.timeline = false;
        updateDuration(false);

        await updateStatus({
            progress: 25,
            msg: m.iconik_import_status_storage_info(),
        });

        let getStorageRes = await httpsCallable(functions, "v8IconikGetStorageList")();
        let iconikStorages = JSON.parse(getStorageRes.data).objects;
        iconikStorages = iconikStorages.filter((s) =>{
            return s.purpose === "FILES" && s.status === "ACTIVE";
        });

        await updateStatus({
            progress: 65,
            msg: m.iconik_import_status_loading_info(),
        });

        if (externalDataState.assetId && externalDataState.status === "importing") {
            /* Handed over by a "closedcaptioncreator://iconik" deep link, where
               the window's own URL is a local file with no params to read. */
            assetId = externalDataState.assetId;
        } else {
            console.info("DEBUG: Parsing URL params from URL: " + window.location.href);
            let urlString = window.location.href;
            let url = new URL(urlString);
            assetId = url.searchParams.get("asset_ids");
        }

        console.info("DEBUG: Asset id is set to " + assetId);

        if (!assetId) {
            throw new Error(m.iconik_import_missing_asset_error());
        }

        let projectRes = await httpsCallable(functions, "v8IconikImport")(assetId);
        projectInfo = JSON.parse(projectRes.data);
        let fileSize = projectInfo?.media?.size;
        
        await updateStatus({
            progress: 35,
            msg: m.iconik_import_status_creating_project(),
        });

        console.info(`DEBUG: Creating new project.`);

        setExternalDataState({
            platform : "Iconik",
            projectName : projectInfo.name,
            frameRate : projectInfo.frameRate,
            dropFrame : projectInfo.dropFrame,
            assetId : assetId,
            mediaUrl : projectInfo.media.path,
            storages : iconikStorages
        });

        console.info("EXTERNAL DATA STORE: ", JSON.stringify(externalDataState, null, 4));

        await updateStatus({
            progress: 50,
            msg: m.iconik_import_status_loading_project(),
        });
        
        resetProjectState({
            ...projectInfo,
            media : {},
            eventGroups : [],
            username : auth.currentUser.email,
            ownerId : auth.currentUser.uid,
            selected : false
        });

        projectInfo.eventGroups.forEach(evg =>{
            eventGroupState.push({...(new _EventGroup(evg))});
            eventGroupState[eventGroupState.length-1].extId = evg.extId;
        });

        projectState.selected = 0;

        if (projectInfo.markers) {
            resetMarkerStateWithData(projectInfo.markers);
        }
        
        await updateStatus({
            progress: 75,
            msg: m.iconik_import_status_loading_media(),
        });

        updateProjectMedia({
            projectName: projectInfo.name,
            storage: projectInfo.media.storage,
            mediaUrl: projectInfo.media.path,
            incode: 0,
            frameRate: projectInfo.frameRate,
            dropFrame: projectInfo.dropFrame,
            aspectRatio: "16/9",
        });

        let mediaType = projectState.media.type === "video/quicktime" ? "video/mp4" : projectState.media.type; 

        if (fileSize) {
            const sizeInBytes = parseInt(fileSize);
            const sizeInKilobytes = sizeInBytes / 1024;
            const sizeInMegabytes = sizeInKilobytes / 1024;
            if (sizeInMegabytes < 512) {
                projectState.media.useFallback = false;
            } else {
                console.log("SETTING PEAKS DATA TO 0");
                projectState.media.peaksData = [[0],[0]];
            }
        } else {
            console.log("SETTING PEAKS DATA TO 0");
            projectState.media.peaksData = [[0],[0]];
        }

        player.src = { src: projectState.media.path, type: mediaType};

        await updateStatus({
            progress: 100,
            msg: m.iconik_import_status_done(),
        });
        
        toast.push(m.iconik_import_success_toast(), {classes: ['toast-success']});
        
        setTimeout(() => {
            uiState.timeline = true;
            hideModal();
        }, 2500);
    } catch (err) {
        console.log(err);
        console.log(err.message);
        toast.push(m.iconik_import_failed_toast({ error: err.message }), {classes: ['toast-danger']});  
        hideModal();      
    }
});
</script>

<div 
    transition:fade={{ duration: 100 }} 
    class="modal {modalState.selected === 'iconikImport' ? 'show d-block' : ''}" 
    role="dialog"
    aria-labelledby="iconikImportTitle"
    aria-describedby="iconikImportDescription" 
    tabindex="-1" 
    id="IconikImportModal">
    <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="iconikImportTitle">{m.iconik_import_title()}</h4>
                <button type="button" 
                        class="btn-close"
                        id="closeIconikImportModal"
                    aria-label={m.iconik_import_close_label()} 
                        onclick={hideModal}></button>
            </div>
            <div class="modal-body" id="iconikImportDescription">
                <p role="status" aria-live="polite">
                    <i class="bi bi-exclamation-diamond-fill" aria-hidden="true"></i> {statusMsg}
                </p>
                <div class="progress">
                    <div class="progress-bar bg-primary progress-bar-striped progress-bar-animated" 
                         role="progressbar" 
                         style="width: {progress}%;" 
                         aria-valuenow={progress} 
                         aria-valuemin="0" 
                         aria-valuemax="100"
                         aria-label={m.iconik_import_progress_label()}>
                        {progress}%
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
